import { useTranslation } from 'react-i18next';
import type { TenderStatus, TenderStatusV2, CvStatus, DpoRequestStatus } from '../types';

type AnyStatus = TenderStatus | TenderStatusV2 | CvStatus | DpoRequestStatus;

interface StatusBadgeProps {
  status: AnyStatus;
}

const STATUS_CLASSES: Record<AnyStatus, string> = {
  // Tender statuses
  draft:      'bg-gray-100  text-gray-600  ring-gray-300',
  submitted:  'bg-blue-100  text-blue-700  ring-blue-300',
  matching:   'bg-amber-100 text-amber-700 ring-amber-300',
  matched:    'bg-teal-100  text-teal-700  ring-teal-300',
  won:        'bg-green-100 text-green-700 ring-green-300',
  lost:       'bg-red-100   text-red-700   ring-red-300',
  archived:   'bg-gray-100  text-gray-500  ring-gray-200',

  // Tender v2 lifecycle statuses (redesign pipeline)
  NEW:                 'bg-gray-100   text-gray-600   ring-gray-300',
  VALIDATED:           'bg-blue-100   text-blue-700   ring-blue-300',
  SECTIONED:           'bg-blue-100   text-blue-700   ring-blue-300',
  TRIAGED:             'bg-indigo-100 text-indigo-700 ring-indigo-300',
  CRITERION_EXTRACTED: 'bg-amber-100  text-amber-700  ring-amber-300',
  PERSONAS_EXTRACTED:  'bg-amber-100  text-amber-700  ring-amber-300',
  SKILLS_EXTRACTED:    'bg-amber-100  text-amber-700  ring-amber-300',
  PROCESSED:           'bg-green-100  text-green-700  ring-green-300',
  REJECTED:            'bg-red-100    text-red-700    ring-red-300',

  // CV statuses
  active:         'bg-green-100 text-green-700 ring-green-300',
  inactive:       'bg-gray-100  text-gray-500  ring-gray-200',
  pending_review: 'bg-amber-100 text-amber-700 ring-amber-300',

  // DPO request statuses
  pending:   'bg-amber-100 text-amber-700 ring-amber-300',
  approved:  'bg-blue-100  text-blue-700  ring-blue-300',
  rejected:  'bg-red-100   text-red-700   ring-red-300',
  completed: 'bg-green-100 text-green-700 ring-green-300',
};

/**
 * StatusBadge renders a colour-coded pill for any status value used in the app.
 * All colour combinations meet WCAG 2.1 AA contrast ratios.
 */
export default function StatusBadge({ status }: StatusBadgeProps) {
  const { t } = useTranslation();
  const classes = STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-600 ring-gray-300';
  // Fallback for statuses without a translation key (e.g. the v2 lifecycle
  // enums): humanize the raw value — "CRITERION_EXTRACTED" -> "Criterion extracted".
  const humanized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, ' ');
  const label = t(`statusBadge.${status}`, humanized);

  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1',
        classes,
      ].join(' ')}
    >
      {label}
    </span>
  );
}
